/**
 * `<SlotCardSlider>` — numeric slider for the `slider` slot type.
 *
 * Anchor labels are read from `slot.slider.anchorMin` /
 * `slot.slider.anchorMax`; the rail itself is a native
 * `<input type="range">` for keyboard a11y. Output is a `number`
 * within `[min, max]`.
 *
 * Default value when no prefill exists: midpoint of the range,
 * rounded toward the lower end. Tests pin this so the engine's
 * "no answer yet" payload is deterministic on Submit.
 */

import type { SlotDefinition } from '@volley/crucible/interview/types.js';

export interface SlotCardSliderProps {
  slot: SlotDefinition;
  value: number;
  onChange: (next: number) => void;
  prefilledUntouched: boolean;
}

/** Pure helper — midpoint default when the engine supplies no prefill. */
export function defaultSliderValue(slot: SlotDefinition): number {
  const cfg = slot.slider;
  if (!cfg) return 0;
  return Math.floor((cfg.min + cfg.max) / 2);
}

export function SlotCardSlider({
  slot,
  value,
  onChange,
  prefilledUntouched,
}: SlotCardSliderProps): React.JSX.Element {
  const cfg = slot.slider;
  if (!cfg) {
    return (
      <div
        data-testid={`slot-card-slider-${slot.id}`}
        data-empty="true"
        className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200/80"
      >
        Slider configuration missing. Skip to continue.
      </div>
    );
  }

  return (
    <div data-testid={`slot-card-slider-${slot.id}`} className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-white/40">
        <span data-testid={`slot-card-slider-${slot.id}-anchor-min`}>{cfg.anchorMin}</span>
        <span
          data-testid={`slot-card-slider-${slot.id}-value`}
          className={prefilledUntouched ? 'text-white/40' : 'text-crucible-accent'}
        >
          {value}
        </span>
        <span data-testid={`slot-card-slider-${slot.id}-anchor-max`}>{cfg.anchorMax}</span>
      </div>
      <input
        type="range"
        min={cfg.min}
        max={cfg.max}
        value={value}
        step={1}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={`slot-card-slider-${slot.id}-input`}
        className={`w-full accent-crucible-accent ${prefilledUntouched ? 'opacity-60' : ''}`}
      />
    </div>
  );
}
